import {
  Box,
  Text,
  useRadio,
  useRadioGroup,
  UseRadioProps,
  VStack,
  HStack,
  StyleProps,
} from '@chakra-ui/react';
import { ReactNode } from 'react';

interface BaseOption {
  value: string;
  label?: string;
}

interface OptionSelectorProps<T extends BaseOption> {
  options: T[];
  value: string;
  onChange: (value: string) => void;
  title?: string;
  direction?: 'vertical' | 'horizontal';
  optionWidth?: string | number;
  renderOption: (option: T, isSelected: boolean) => ReactNode;
  spacing?: number;
  containerProps?: StyleProps;
  optionContainerProps?: StyleProps;
}

interface RadioCardProps extends UseRadioProps {
  optionWidth?: string | number;
  children: ReactNode;
}

const RadioCard = ({ optionWidth = 'full', children, ...props }: RadioCardProps) => {
  const { getInputProps, getRadioProps } = useRadio(props);

  const input = getInputProps();
  const radioProps = getRadioProps();

  return (
    <Box
      as='label'
      w={optionWidth}
    >
      <input {...input} />
      <Box
        {...radioProps}
        _checked={{
          bg: 'primary.light',
          borderColor: 'primary.pure',
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        bg='bg.pure'
        borderRadius='md'
        borderWidth='1px'
        boxShadow='md'
        cursor='pointer'
        p='sm'
        w='full'
        {...props}
      >
        {children}
      </Box>
    </Box>
  );
};

const OptionSelector = <T extends BaseOption>({
  options,
  value,
  onChange,
  title,
  direction = 'vertical',
  renderOption,
  spacing = 2,
  containerProps = {},
  optionContainerProps,
  optionWidth,
}: OptionSelectorProps<T>) => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    value: value.toString(),
    onChange: (value) => onChange(value),
  });

  const OptionsContainer = direction === 'vertical' ? VStack : HStack;
  const optionsAlignment = direction === 'vertical' ? 'start' : 'center';

  return (
    <VStack
      align='start'
      bgColor='bg.light'
      borderRadius='xl'
      borderWidth='1px'
      pb='lg'
      pt='md'
      px='md'
      spacing={spacing}
      w='full'
      {...containerProps}
      {...getRootProps()}
    >
      {title && (
        <Text
          fontWeight='semibold'
          mb='xs'
        >
          {title}
        </Text>
      )}
      <OptionsContainer
        align={optionsAlignment}
        spacing={spacing}
        w='full'
      >
        {options.map((option) => {
          const radioProps = getRadioProps({ value: option.value.toString() });
          const isSelected = option.value.toString() === value.toString();

          return (
            <RadioCard
              key={option.value}
              {...optionContainerProps}
              {...radioProps}
              optionWidth={optionWidth}
            >
              {renderOption(option, isSelected)}
            </RadioCard>
          );
        })}
      </OptionsContainer>
    </VStack>
  );
};

export type { BaseOption };
export default OptionSelector;
